import { inRow, onLine, place, takeBack, tiles } from "./fuxi-tile-moves.js";
import { isChoosing, optionsOf, requirePart } from "./lianxi-yufa-item.js";
import { optionByKey, ownEnter } from "./lianxi-xuanze.js";
import { dragTiles } from "./tile-drag.js";
import { focusedGroup, pickFocused } from "./lianxi-yufa-focused.js";
import { updateRepairInput } from "./lianxi-yufa-repair.js";

/**
 * Moving the tiles of a grammar exercise's arrangement, by pointer or by key,
 * and answering the item from the keyboard.
 *
 * The moves are `./fuxi-tile-moves.ts`, the ones a 复习 word-order card
 * makes, and this is the listening, which `./fuxi-tiles.ts` does for a card.
 * A card is one at a time on its page, and the listener there finds the first
 * tiles on the page. An exercise holds every item's tiles, hidden but one, so
 * the keys go to the item that is showing. The presses are heard on the
 * block and the keys on the document, since a key pressed with nothing
 * focused lands on the body, which is outside the block.
 *
 * **Enter presses Check, or Next once the item is marked**, so an item is
 * answered from the keyboard the way a card is. A gap's field submits its
 * form on Enter by itself, and an arrangement and a Choose item have no
 * field, so Enter is heard here for them. A letter or a digit chooses an
 * option of an item answered by choosing, through `choose`, which marks a
 * right one at once, and a digit places an arrangement's tile. The last tile
 * placed by pointer hands the caret to Check. The
 * card's own move looks for a `.cb-fuxi-check` in the form and the
 * exercise's button is its own, so the caret is moved here once the row is
 * empty.
 */

/**
 * Whether the key was meant for something the reader is typing into.
 *
 * A Choose item's radio buttons are inputs a reader types nothing into, so
 * a digit or Enter pressed on one is heard here.
 */
function isTyping(target: EventTarget | null): boolean {
  return (
    (target instanceof HTMLInputElement && target.type !== "radio") ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/** The digit a key names, counting from nought, or nothing. */
function digitOf(key: string): number | undefined {
  return /^[1-9]$/u.test(key) ? Number(key) - 1 : undefined;
}

/** Choose an option of the item showing, which marks it where it is right. */
export type Choose = (form: HTMLFormElement, option: number) => void;

/** The item that is showing, which is the one form not hidden. */
function showing(block: HTMLElement): HTMLFormElement | undefined {
  return [...block.querySelectorAll<HTMLFormElement>("form")].find(
    (form) => form.hidden === false,
  );
}

/** Place a tile, and hand the caret to Check once the row is empty. */
function placeTile(card: Element, tile: HTMLElement, moveCaret: boolean): void {
  place(card, tile, moveCaret);

  const waiting = card.querySelector(`${inRow}:not([disabled])`);

  if (moveCaret && waiting === null) {
    requirePart(card.closest("form")!, "check").focus();
  }
}

/**
 * Check the item, or press Next once it is marked. An item of options has no
 * Check showing, and Enter answers it with the option an arrow moved to.
 */
function pressOn(form: HTMLFormElement): void {
  if (form.dataset["cbYufaAnswered"] === undefined) {
    form.requestSubmit();
  } else {
    requirePart(form, "next").click();
  }
}

/** A tile placed by its digit, or the last taken back, on the showing item. */
function moveByKey(card: Element, event: KeyboardEvent): void {
  if (event.key === "Backspace") {
    const last = [...card.querySelectorAll(onLine)].at(-1);

    if (last !== undefined) {
      event.preventDefault();
      takeBack(card, last, false);
    }

    return;
  }

  const digit = digitOf(event.key) ?? -1;
  const tile = card.querySelector<HTMLElement>(
    `[data-cb-fuxi-tile="${String(digit)}"]:not([disabled])`,
  );

  if (tile !== null) {
    event.preventDefault();
    placeTile(card, tile, false);
  }
}

/**
 * Move the tiles of whichever item is showing, by pointer, by dragging or by key, and
 * answer it on Enter.
 * @param block - The exercise's block.
 */
export function wireTiles(block: HTMLElement, choose: Choose): void {
  dragTiles(block);
  block.addEventListener("change", (event) => {
    const form =
      event.target instanceof Element ? event.target.closest("form") : null;
    if (form !== null) {
      updateRepairInput(form);
    }
  });
  block.addEventListener("click", (event: Event) => {
    const pressed = event.target as Element;
    if (pressed.closest("[data-cb-yufa-answered]")) {
      return;
    }
    const tile = pressed.closest<HTMLElement>(`${inRow}:not([disabled])`);
    const placed = pressed.closest(onLine);

    if (tile !== null) {
      placeTile(tile.closest(tiles)!, tile, true);
    } else if (placed !== null) {
      takeBack(placed.closest(tiles)!, placed, true);
    }
  });

  block.ownerDocument.addEventListener("keydown", (event: KeyboardEvent) => {
    const form = showing(block);
    const card = form?.querySelector(tiles);

    if (
      form === undefined ||
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      isTyping(event.target)
    ) {
      return;
    }

    if (event.key === "Enter") {
      if (
        !ownEnter(event.target) &&
        !(
          event.target instanceof Element &&
          event.target.closest("wa-details") !== null
        )
      ) {
        event.preventDefault();
        pressOn(form);
      }
    } else if (form.dataset["cbYufaAnswered"] !== undefined) {
      return;
    } else if (card !== null && card !== undefined) {
      moveByKey(card, event);
    } else if (isChoosing(form)) {
      const option = optionByKey(event.key, optionsOf(form).length);
      if (option !== undefined) {
        event.preventDefault();
        choose(form, option);
      }
    } else if (focusedGroup(form) !== null) {
      const digit = digitOf(event.key);
      if (digit !== undefined) {
        event.preventDefault();
        pickFocused(form, digit);
      }
    }
  });
}
